import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';
import { X } from 'lucide-react';
import { IconButton } from '@/components/ui';
import { Wordmark } from '@/components/brand';
import { cn } from '@/lib/utils';
import { SHIBA_PANEL_ID } from './ShibaPanel';
import { Sparkles } from './Sparkles';
import { DRAG_SLOP } from './useShibaFrame';

/**
 * What waits behind a collapsed pill. Not a count: a send is blocked while an
 * answer streams and opening clears it, so at most ONE turn can be waiting —
 * either the answer or the failure that asks for a retry.
 */
export type ShibaUnread = 'answer' | 'failure' | null;

/**
 * Só o verbo: o logo ao lado já diz quem responde, então repetir o nome na
 * pílula seria dizer "Shiba" duas vezes. A pílula inteira é a ação, e o rótulo
 * é o nome dela.
 */
const LABEL = 'Pergunte algo';

/**
 * A frase que explica o assistente. Ela saiu da TELA (a pílula agora é compacta),
 * não da árvore de acessibilidade: continua sendo a descrição da ação, com uma
 * variante para cada estado de não lida.
 */
const TEASER = {
  rest: 'Posso ajudar com a sua base de conhecimento e a plataforma',
  answer: 'Nova resposta esperando por você',
  failure: 'Não consegui responder. Abra para tentar de novo',
} as const;

/** The dot is a dot; this is what assistive tech reads in its place. */
const BADGE_LABEL = {
  answer: 'Nova mensagem da Shiba',
  failure: 'A Shiba não conseguiu responder',
} as const;

const TEASER_ID = 'shiba-teaser';
const BADGE_ID = 'shiba-badge';

export interface ShibaLauncherProps {
  onOpen: () => void;
  /** What landed while the panel was collapsed, if anything. */
  unread: ShibaUnread;
  /** Focus returns here when the USER collapses the panel. */
  ctaRef?: RefObject<HTMLButtonElement>;
  /** Starts a frame drag: the whole pill is the grip. */
  onGrab: (event: PointerEvent) => void;
  /**
   * "Ocultar": tira a pílula da tela (a volta é pelo menu da conta). O widget
   * sempre passa; sem ele, a pílula é só a ação (ex.: renderizada isolada).
   */
  onHide?: () => void;
}

/**
 * Collapsed state of the Shiba widget: the pill. The white ∫hibΔ wordmark on the
 * brand purple (plus, when a turn finished out of sight, the unread badge at its
 * lower right) and a one-line label. The pill is a single button: pressing it
 * opens the panel, dragging it moves the frame — see {@link ShibaLauncher} body
 * for how one pointer press serves both.
 *
 * Sobre ela, as faíscas de IA ({@link Sparkles}), as mesmas da pílula do site:
 * irmãs do botão, na caixa da pílula (à frente dele, atrás do "Ocultar"), então
 * somem junto com ela quando o painel abre. O ponto verde de "online" (que nunca mudava) saiu — cor semântica é para
 * estado, e o único ponto que sobra é o de novidade. Ao lado, o "Ocultar": quem
 * tem a paginação debaixo da pílula precisa de um jeito de tirá-la do caminho.
 */
export function ShibaLauncher({
  onOpen,
  unread,
  ctaRef,
  onGrab,
  onHide,
}: ShibaLauncherProps): JSX.Element {
  /**
   * A pílula é ao mesmo tempo a AÇÃO e a alça de ARRASTE, então os dois gestos
   * dividem um único `pointerdown`: ele arma o arraste (`onGrab`, como antes) e
   * passa a medir o deslocamento. Acima de DRAG_SLOP px o gesto é promovido a
   * arraste e o `click` que o navegador ainda dispara no fim é descartado.
   *
   * É o ponto mais fácil de regredir aqui. Sem o limiar, todo arraste terminaria
   * abrindo o painel; sem armar já no `pointerdown`, a pílula deixaria de
   * arrastar — antes o corpo arrastava e o botão, separado, não; agora eles são a
   * mesma superfície e não há um segundo alvo para onde empurrar o gesto.
   */
  const draggedRef = useRef(false);
  const releaseRef = useRef<() => void>();
  // Um gesto em voo quando o launcher desmonta (abrir o painel, por exemplo) não
  // pode deixar ouvintes pendurados na janela.
  useEffect(() => () => releaseRef.current?.(), []);

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    if (event.button === 0) {
      // `onGrab` cancela este pointerdown (senão o arraste vira seleção de texto)
      // e, junto com o padrão, morre o mousedown de compatibilidade que daria foco
      // ao botão. Sem este foco explícito a ação só seria alcançável pelo teclado.
      event.currentTarget.focus();
      releaseRef.current?.();
      const startX = event.clientX;
      const startY = event.clientY;
      draggedRef.current = false;

      const onMove = (move: MouseEvent): void => {
        const far =
          Math.abs(move.clientX - startX) > DRAG_SLOP ||
          Math.abs(move.clientY - startY) > DRAG_SLOP;
        if (far) draggedRef.current = true;
      };
      const release = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', release);
        window.removeEventListener('pointercancel', release);
        releaseRef.current = undefined;
      };
      releaseRef.current = release;
      // Na janela, e não no botão: o movimento que decide o gesto acontece
      // justamente fora da caixa da pílula.
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    }
    onGrab(event);
  };

  return (
    <>
      {/* A caixa tem o tamanho da pílula: é a referência das faíscas e do "Ocultar". */}
      <div className="shiba-launcher-box">
        <Sparkles />
        <button
          ref={ctaRef}
          type="button"
          className={cn(
            'shiba-launcher',
            'shiba__grip',
            unread === 'failure' && 'shiba-launcher--failure',
          )}
          // O rótulo desaparece da tela em telas estreitas (ver Shiba.css, ≤480px):
          // o nome acessível é explícito aqui para nunca depender do que está visível.
          aria-label={LABEL}
          // A semântica sobrevive à troca de estado: esta ação e o "recolher" do painel são as
          // duas metades da MESMA divulgação e apontam para o mesmo id. O painel é
          // deliberadamente não-modal (`aria-modal="false"`): o foco nunca é contido,
          // porque a página por baixo continua utilizável com a Shiba aberta.
          aria-expanded="false"
          aria-controls={SHIBA_PANEL_ID}
          // A novidade primeiro, a explicação depois — ambas em texto sr-only, porque
          // um ponto colorido não diz nada a quem não vê e a pílula compacta não tem
          // mais manchete na tela.
          aria-describedby={unread ? `${BADGE_ID} ${TEASER_ID}` : TEASER_ID}
          onPointerDown={onPointerDown}
          onClick={(event) => {
            // Só o clique que o navegador dispara no FIM de um arraste é descartado,
            // e a marca é consumida aqui. Ativação por teclado chega com
            // `detail === 0` (não houve gesto de ponteiro) e passa sempre: sem essa
            // ressalva, um arraste deixaria o Enter mudo até o próximo clique.
            const endedADrag = event.detail !== 0 && draggedRef.current;
            draggedRef.current = false;
            if (!endedADrag) onOpen();
          }}
        >
          <span className="shiba__mark">
            {/* Decorativo de propósito: o rótulo ao lado já nomeia a ação, e um
                segundo nome só duplicaria o anúncio. */}
            <span className="shiba__logo" aria-hidden="true">
              <Wordmark size="h24" tone="inverse" />
            </span>
            {unread && (
              <span className="shiba__badge">
                {/* Forma, não só cor (WCAG 1.4.1): âmbar e vermelho caem justamente
                    no eixo de confusão protan/deutan, então a falha carrega também
                    um "!" — e um ponto maior — para não virar a mesma bolinha da
                    resposta que chegou. O glifo é decoração do rótulo sr-only ao
                    lado, que é quem realmente nomeia o estado. */}
                {unread === 'failure' && (
                  <span className="shiba__badge-glyph" aria-hidden="true">
                    !
                  </span>
                )}
                <span className="sr-only" id={BADGE_ID}>
                  {BADGE_LABEL[unread]}
                </span>
              </span>
            )}
          </span>

          <span className="shiba-launcher__label">{LABEL}</span>
        </button>

        {/* Irmão da pílula, não filho: um botão não cabe dentro de outro. Vem
            depois dela no Tab e aparece com o ponteiro sobre a pílula, com o foco
            do teclado ou sempre, em telas de toque (ver Shiba.css). */}
        {onHide && (
          <IconButton
            size="sm"
            variant="secondary"
            label="Ocultar a Shiba"
            icon={<X />}
            className="shiba-launcher__hide"
            onClick={onHide}
          />
        )}
      </div>

      <p className="sr-only" id={TEASER_ID}>
        {TEASER[unread ?? 'rest']}
      </p>
    </>
  );
}
